/**
 * The ONE look of every dialog, popup and simple dropdown (docs/THEME.md 1.4, 1.5, 2.9, 2.10, 2.16; STANDARD v3,
 * owner decision ADOPT-AS-TOKEN). Every overlay primitive (dialog.tsx sections layout, alert-dialog.tsx, select.tsx
 * look="standard", dropdown-menu.tsx look="standard", popover.tsx, combobox.tsx, simple-select.tsx) builds from these
 * recipes and nothing else, so they cannot drift apart again.
 *
 * Every value is a named token (styles/design-tokens.css sections 8 and 9). Feature code never reads these: it uses
 * the primitives. The gallery (app/design-system/overlays) renders the table below next to the real primitives.
 */

const DRAWER_WIDTH_CAP = 'max-w-[calc(100vw-3.5rem)]';

/** The one focus recipe (1.1): ring plus a transparent outline, so focus survives Windows forced colours. */
export const FOCUS_RING =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-transparent focus-visible:ring-2 focus-visible:ring-edge-focus';

export type OverlaySize = 'sm' | 'md' | 'lg';
export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

/** Popup surface: menu, select list, popover, listbox (2.10). */
const popupSurface = 'bg-popup border border-edge rounded-overlay shadow-float p-1 text-body text-ink';

/** One option row (Select item, menu item, listbox option): 30px minimum, highlighted = hover fill (2.10). */
const option =
  'relative flex w-full min-h-control-sm cursor-default select-none items-center gap-2 rounded-item px-2.5 text-body text-ink outline-none data-[highlighted]:bg-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50';

/** The select trigger (2.16): raised control, visible control edge, field text, one focus recipe. */
const trigger =
  `flex w-full items-center justify-between gap-2 rounded-item border border-control-edge bg-raised px-2.5 text-left text-field text-ink data-[placeholder]:text-ink-muted disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-danger [&>span]:line-clamp-1 ${FOCUS_RING}`;

export const overlay = {
  popupSurface,
  /** Never wider than the viewport minus the 8px collision padding on each side. */
  popupMaxWidth: 'max-w-[calc(100vw-1rem)]',
  popupWidth: { sm: 'w-popup-sm', md: 'w-popup', lg: 'w-popup-lg' } as Record<OverlaySize, string>,
  option,
  /** A checked option (Select item, checkbox/radio item, multi-select option). */
  optionChecked: 'data-[state=checked]:bg-selected aria-selected:bg-selected',
  /** Room for the check / radio indicator (the inset is preserved, 2.10). */
  optionInset: 'pl-8',
  optionIndicator: 'absolute left-2.5 flex icon items-center justify-center',
  optionIcon: 'icon shrink-0 text-ink-muted',
  /** A short description under the label. ink-secondary, never ink-muted, on a hovered popup row (1.1). */
  optionDescription: 'block text-meta text-ink-secondary',
  groupLabel: 'px-2.5 pt-2 pb-1 text-section uppercase text-ink-muted',
  separator: '-mx-1 my-1 h-px bg-edge',
  shortcut: 'ml-auto text-chip text-ink-muted',
  /** Rich option slots (MenuOptionContent, SelectItem): a tinted square behind the leading icon. */
  optionIconTile: 'flex size-6 shrink-0 items-center justify-center rounded-item',
  /** A neutral leading icon with no tone (the tile without a tone uses the raised fill). */
  optionIconTileNeutral: 'bg-raised text-ink-muted',
  /** A colour swatch before the label (a category, a source, a status colour). */
  optionSwatch: 'size-3 shrink-0 rounded-item',
  /** A trailing count or tag, list only. */
  optionBadge: 'ml-auto shrink-0 rounded-item bg-raised px-1.5 text-chip text-ink-secondary tabular-nums',
  /** The trailing dot on the current item. */
  optionMarker: 'ml-auto size-1.5 shrink-0 rounded-full',
  /** Nested options (a department under its agency): the indent per depth, after the indicator inset. */
  optionDepth: { 1: 'pl-12', 2: 'pl-16' } as Record<1 | 2, string>,
  /** The leading dot of a toned group label. */
  groupLabelDot: 'inline-block size-1.5 shrink-0 rounded-full',
  emptyText: 'px-2.5 py-2 text-meta text-ink-muted',
  searchInput: `mb-1 w-full min-h-control-sm rounded-item border border-control-edge bg-raised px-2.5 text-field text-ink placeholder:text-ink-muted ${FOCUS_RING}`,

  trigger,
  triggerSize: { sm: 'min-h-control-sm', md: 'min-h-control', lg: 'min-h-control-lg' } as Record<OverlaySize, string>,
  triggerIcon: 'icon shrink-0 text-ink-muted',
  /** A leading icon in a select trigger (a filter icon). Overrides the trigger's `[&>span]:line-clamp-1`. */
  triggerLeading: '!inline-flex !overflow-visible shrink-0 items-center text-ink-muted',
  /** A trigger whose filter is set (not the "all" value): the brand edge and the selected fill. */
  triggerActive: 'border-brand bg-selected',

  /** Dialog/sheet backdrop ONLY (1.1). */
  backdrop: 'fixed inset-0 bg-backdrop',
  /** Opt-in for a page state that must keep its previous opaque cover (call-left). */
  backdropOpaque: 'fixed inset-0 bg-canvas',
  /** The non-scrolling dialog root of layout="sections" (2.9). */
  dialogSurface:
    'pointer-events-auto relative flex w-full max-h-[calc(100dvh-2rem)] flex-col bg-panel border border-edge rounded-overlay shadow-modal text-ink outline-none',
  dialogWidth: { sm: 'max-w-dialog-sm', md: 'max-w-dialog-md', lg: 'max-w-dialog-lg', xl: 'max-w-dialog-xl' } as Record<DialogSize, string>,
  /** Centres the dialog; top-aligned on short viewports so the keyboard never hides the header. */
  dialogPositioner: 'pointer-events-none fixed inset-0 flex items-start justify-center p-2 sm:p-4 [@media(min-height:30rem)]:items-center',
  /**
   * A slide-out DRAWER on the same dialog shell (layout="sections" side="left"): flush with the left edge, full height,
   * 18rem wide (never wider than the viewport), the panel surface with one right edge, the dialog
   * shadow, and the slide from / to the left. Same backdrop, depth band, focus scope and layer slot as any dialog.
   */
  /**
   * Width cap shared by every slide-out drawer: the drawer never covers the whole phone, so a strip of the page (3.5rem, 70px at the
   * 20px root, the rail's width) stays tappable as the scrim that dismisses it. Design choice, one place to change.
   */
  drawerWidthCap: DRAWER_WIDTH_CAP,
  drawerPositioner: 'pointer-events-none fixed inset-y-0 left-0 flex h-dvh',
  drawerSurface:
    `pointer-events-auto relative flex h-full w-72 ${DRAWER_WIDTH_CAP} flex-col bg-panel border-r border-edge shadow-modal text-ink outline-none pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left`,
  drawerClose: 'top-[calc(0.75rem+env(safe-area-inset-top))] right-[max(0.75rem,env(safe-area-inset-right))]',
  dialogHeader: 'flex shrink-0 flex-col gap-1.5 px-5 pt-5 pb-3 pr-12',
  dialogBody: 'min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-1',
  /** Persistent actions: cancel then primary, end-aligned; stacked full width (primary on top) below sm. */
  dialogFooter: 'flex shrink-0 flex-col-reverse gap-2 px-5 pt-3 pb-5 sm:flex-row sm:justify-end',
  dialogTitle: 'text-heading text-ink',
  dialogDescription: 'text-meta text-ink-muted',
  dialogClose: `absolute right-3 top-3 inline-flex size-control-sm items-center justify-center rounded-item text-ink-muted hover:bg-hover hover:text-ink disabled:pointer-events-none ${FOCUS_RING}`,

  /** The non-modal inspector (floating-panel.tsx, 2.9): panel surface, float shadow, its caller-owned rect. */
  floatingPanel: 'fixed flex flex-col bg-panel border border-edge rounded-overlay shadow-float text-ink text-chip font-normal select-none',
  /** Its drag bar: a raised strip across the top, the hover fill while hovered or dragged. */
  floatingBar:
    'flex h-7 shrink-0 cursor-move items-center gap-2 rounded-t-overlay border-b border-edge bg-raised px-2 transition-colors hover:bg-hover-raised data-[dragging]:bg-hover-raised',
  floatingTitle: 'min-w-0 flex-1 truncate text-micro text-ink-muted',
  floatingClose: `inline-flex size-6 shrink-0 items-center justify-center rounded-item text-ink-muted hover:bg-hover hover:text-ink ${FOCUS_RING}`,
  /** The resize corner: a small triangle in the muted ink. */
  floatingResize:
    'absolute bottom-0 right-0 size-3 cursor-se-resize rounded-br-overlay bg-[linear-gradient(135deg,transparent_50%,var(--color-ink-muted)_50%)] opacity-40 transition-opacity hover:opacity-80',
} as const;

/**
 * The colour of a rich option part (icon, swatch, label, marker), as tokens only: the status family and the seven
 * category colours (styles/design-tokens.css). Literal class strings so Tailwind sees every one.
 */
export type OptionTone =
  | 'brand' | 'info' | 'success' | 'warning' | 'danger'
  | 'cat-1' | 'cat-2' | 'cat-3' | 'cat-4' | 'cat-5' | 'cat-6' | 'cat-7';

export const OPTION_TONES: Record<OptionTone, { text: string; tile: string; dot: string }> = {
  brand: { text: 'text-brand', tile: 'bg-brand-muted text-brand', dot: 'bg-brand' },
  info: { text: 'text-info', tile: 'bg-info-soft text-info', dot: 'bg-info' },
  success: { text: 'text-success', tile: 'bg-success-soft text-success', dot: 'bg-success' },
  warning: { text: 'text-warning', tile: 'bg-warning-soft text-warning', dot: 'bg-warning' },
  danger: { text: 'text-danger', tile: 'bg-danger-soft text-danger', dot: 'bg-danger' },
  'cat-1': { text: 'text-cat-1', tile: 'bg-cat-1/15 text-cat-1', dot: 'bg-cat-1' },
  'cat-2': { text: 'text-cat-2', tile: 'bg-cat-2/15 text-cat-2', dot: 'bg-cat-2' },
  'cat-3': { text: 'text-cat-3', tile: 'bg-cat-3/15 text-cat-3', dot: 'bg-cat-3' },
  'cat-4': { text: 'text-cat-4', tile: 'bg-cat-4/15 text-cat-4', dot: 'bg-cat-4' },
  'cat-5': { text: 'text-cat-5', tile: 'bg-cat-5/15 text-cat-5', dot: 'bg-cat-5' },
  'cat-6': { text: 'text-cat-6', tile: 'bg-cat-6/15 text-cat-6', dot: 'bg-cat-6' },
  'cat-7': { text: 'text-cat-7', tile: 'bg-cat-7/15 text-cat-7', dot: 'bg-cat-7' },
};

/**
 * A Tailwind palette name from data (a nav colour scheme, a search source colour, a filter colour) to its category
 * tone (cat-1 blue, cat-2 emerald, cat-3 amber, cat-4 purple, cat-5 cyan, cat-6 orange, cat-7 red). Neutral names
 * (slate, gray, ...) and unknown ones have no tone.
 */
export function toneFromColorName(name: string | null | undefined): OptionTone | undefined {
  switch (name) {
    case 'blue': case 'sky': case 'indigo': return 'cat-1';
    case 'emerald': case 'green': case 'lime': case 'teal': return 'cat-2';
    case 'amber': case 'yellow': return 'cat-3';
    case 'purple': case 'violet': case 'fuchsia': case 'pink': return 'cat-4';
    case 'cyan': return 'cat-5';
    case 'orange': return 'cat-6';
    case 'red': case 'rose': return 'cat-7';
    default: return undefined;
  }
}

/** The token table the gallery shows: every value an overlay uses, by role. */
export const OVERLAY_TOKENS: { role: string; token: string; value: string }[] = [
  { role: 'Popup surface', token: 'bg-popup', value: 'rgb 28 33 40' },
  { role: 'Dialog surface', token: 'bg-panel', value: 'rgb 22 27 34' },
  { role: 'Backdrop', token: 'bg-backdrop', value: 'black 80%' },
  { role: 'Edge', token: 'border-edge', value: 'rgb 48 54 61' },
  { role: 'Control edge (trigger, search)', token: 'border-control-edge', value: 'rgb 118 131 144' },
  { role: 'Popup and dialog radius', token: 'rounded-overlay', value: '5px' },
  { role: 'Option, trigger and close radius', token: 'rounded-item', value: '3px' },
  { role: 'Popup shadow', token: 'shadow-float', value: 'dark drop + soft glow + 1px hairline' },
  { role: 'Dialog shadow', token: 'shadow-modal', value: 'dark drop + soft glow + 1px hairline' },
  { role: 'Popup padding', token: 'p-1', value: '5px' },
  { role: 'Option row', token: 'min-h-control-sm px-2.5', value: '30px min height, 12.5px sides' },
  { role: 'Option highlighted', token: 'bg-hover', value: 'rgb 48 54 61 60%' },
  { role: 'Option checked', token: 'bg-selected', value: 'brand-600 15%' },
  { role: 'Option text / description', token: 'text-body / text-meta text-ink-secondary', value: '15px / 13px' },
  { role: 'Group label', token: 'text-section text-ink-muted', value: '12px uppercase' },
  { role: 'Trigger heights', token: 'min-h-control-sm | control | control-lg', value: '30 / 35 / 40px' },
  { role: 'Dialog widths', token: 'max-w-dialog-sm | md | lg | xl', value: '480 / 640 / 840 / 1120px' },
  { role: 'Dialog padding', token: 'px-5, header pt-5, footer pb-5, gap-2', value: '25px sides, 10px between actions' },
  { role: 'Dialog title / description', token: 'text-heading / text-meta text-ink-muted', value: '17.5px / 13px' },
  { role: 'Button placement', token: 'dialogFooter', value: 'cancel, then primary on the right; stacked below sm' },
  { role: 'Popup widths', token: 'w-popup-sm | popup | popup-lg', value: '220 / 280 / 340px' },
  { role: 'Layer bands', token: 'z-page-layer, modal depth n', value: '1000; 10000/10001, 100100/100101, +100 per depth' },
];
